import React, { createContext, useContext, useReducer, ReactNode } from 'react';

type Settings = {
  videoLength: number;
  fps: number;
  quality: 'low' | 'medium' | 'high' | 'ultra' | string;
  style: 'realistic' | 'artistic' | 'anime' | 'cartoon' | string;
  motionStrength: number;
  useAdvanced: boolean;
  seed: string;
  noiseLevel: number;
};

const defaultSettings: Settings = {
  videoLength: 3,
  fps: 24,
  quality: 'medium',
  style: 'realistic',
  motionStrength: 50,
  useAdvanced: false,
  seed: '',
  noiseLevel: 20,
};

export type HistoryItem = {
  id: string | null;
  prompt: string;
  image?: string | null;
  video: string | null;
  timestamp: string;
  settings: Settings;
};

export type AppState = {
  isGenerating: boolean;
  generationProgress: number;
  generationTaskId: string | null;
  uploadedImage: { file: File; preview: string } | null;
  uploadedSecondImage: { file: File; preview: string } | null;
  generatedVideo: string | null;
  upscaleVideo: string | null;
  addSoundVideo: string | null;
  prompt: string;
  settings: Settings;
  generationHistory: HistoryItem[];
  activeTab: number;
  errorMessage: string;
};

const initialState: AppState = {
  isGenerating: false,
  generationProgress: 0,
  generationTaskId: null,
  uploadedImage: null,
  uploadedSecondImage: null,
  generatedVideo: null,
  upscaleVideo: null,
  addSoundVideo: null,
  prompt: '',
  settings: { ...defaultSettings },
  generationHistory: [],
  activeTab: 0,
  errorMessage: '',
};

const ActionTypes = {
  SET_UPLOADED_IMAGE: 'SET_UPLOADED_IMAGE',
  REMOVE_UPLOADED_IMAGE: 'REMOVE_UPLOADED_IMAGE',
  SET_UPLOADED_SECOND_IMAGE: 'SET_UPLOADED_SECOND_IMAGE',
  REMOVE_UPLOADED_SECOND_IMAGE: 'REMOVE_UPLOADED_SECOND_IMAGE',
  SET_PROMPT: 'SET_PROMPT',
  START_GENERATION: 'START_GENERATION',
  UPDATE_GENERATION_PROGRESS: 'UPDATE_GENERATION_PROGRESS',
  COMPLETE_GENERATION: 'COMPLETE_GENERATION',
  CANCEL_GENERATION: 'CANCEL_GENERATION',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  RESET_FORM: 'RESET_FORM',
  PREPARE_NEW_GENERATION: 'PREPARE_NEW_GENERATION',
  PREPARE_NEW_GENERATION_NO_HISTORY: 'PREPARE_NEW_GENERATION_NO_HISTORY',
  SET_ERROR_MESSAGE: 'SET_ERROR_MESSAGE',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',
  RESET_SETTINGS: 'RESET_SETTINGS',
  DELETE_HISTORY_ITEM: 'DELETE_HISTORY_ITEM',
  LOAD_HISTORY_VIDEO: 'LOAD_HISTORY_VIDEO',
  SET_UPSCALE_VIDEO: 'SET_UPSCALE_VIDEO',
  SET_ADD_SOUND_VIDEO: 'SET_ADD_SOUND_VIDEO',
} as const;

type Action =
  | { type: 'SET_UPLOADED_IMAGE'; payload: { file: File; preview: string } }
  | { type: 'REMOVE_UPLOADED_IMAGE' }
  | { type: 'SET_UPLOADED_SECOND_IMAGE'; payload: { file: File; preview: string } }
  | { type: 'REMOVE_UPLOADED_SECOND_IMAGE' }
  | { type: 'SET_PROMPT'; payload: string }
  | { type: 'START_GENERATION'; payload: { taskId: string } }
  | { type: 'UPDATE_GENERATION_PROGRESS'; payload: number }
  | { type: 'COMPLETE_GENERATION'; payload: { videoUrl: string } }
  | { type: 'CANCEL_GENERATION' }
  | { type: 'SET_ACTIVE_TAB'; payload: number }
  | { type: 'RESET_FORM' }
  | { type: 'PREPARE_NEW_GENERATION' }
  | { type: 'PREPARE_NEW_GENERATION_NO_HISTORY' }
  | { type: 'SET_ERROR_MESSAGE'; payload: string }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<Settings> }
  | { type: 'RESET_SETTINGS' }
  | { type: 'DELETE_HISTORY_ITEM'; payload: string }
  | { type: 'LOAD_HISTORY_VIDEO'; payload: { videoUrl: string } }
  | { type: 'SET_UPSCALE_VIDEO'; payload: { videoUrl: string } }
  | { type: 'SET_ADD_SOUND_VIDEO'; payload: { videoUrl: string } };

function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_UPLOADED_IMAGE':
      return { ...state, uploadedImage: action.payload };
    case 'REMOVE_UPLOADED_IMAGE':
      return { ...state, uploadedImage: null };
    case 'SET_UPLOADED_SECOND_IMAGE':
      return { ...state, uploadedSecondImage: action.payload };
    case 'REMOVE_UPLOADED_SECOND_IMAGE':
      return { ...state, uploadedSecondImage: null };
    case 'SET_PROMPT':
      return { ...state, prompt: action.payload };
    case 'START_GENERATION':
      return {
        ...state,
        isGenerating: true,
        generationProgress: 0,
        generationTaskId: action.payload.taskId,
      };
    case 'UPDATE_GENERATION_PROGRESS':
      return { ...state, generationProgress: action.payload };
    case 'COMPLETE_GENERATION': {
      const videoUrl = action.payload.videoUrl;
      if (!videoUrl || (typeof videoUrl === 'string' && videoUrl.length === 0)) {
        console.error('Invalid video URL received:', videoUrl);
        return { ...state, isGenerating: false, errorMessage: 'Invalid video data received' };
      }
      const newHistory: HistoryItem[] = [
        {
          id: state.generationTaskId,
          prompt: state.prompt,
          image: state.uploadedImage?.preview,
          video: videoUrl,
          timestamp: new Date().toISOString(),
          settings: { ...state.settings },
        },
        ...state.generationHistory,
      ].slice(0, 10);
      return {
        ...state,
        isGenerating: false,
        generatedVideo: videoUrl,
        generationHistory: newHistory,
      };
    }
    case 'CANCEL_GENERATION':
      return { ...state, isGenerating: false, generationProgress: 0, generationTaskId: null };
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    case 'RESET_FORM':
      return { ...state, uploadedImage: null, uploadedSecondImage: null, prompt: '', generatedVideo: null };
    case 'PREPARE_NEW_GENERATION': {
      let updatedHistory = state.generationHistory;
      if (state.generationTaskId) {
        const newHistoryItem: HistoryItem = {
          id: state.generationTaskId,
          prompt: state.prompt,
          image: state.uploadedImage?.preview,
          video: state.generatedVideo || null,
          timestamp: new Date().toISOString(),
          settings: { ...state.settings },
        };
        updatedHistory = [newHistoryItem, ...state.generationHistory].slice(0, 10);
      }
      return {
        ...state,
        isGenerating: false,
        generationProgress: 0,
        generationTaskId: null,
        generatedVideo: null,
        generationHistory: updatedHistory,
        errorMessage: '',
      };
    }
    case 'PREPARE_NEW_GENERATION_NO_HISTORY':
      return {
        ...state,
        isGenerating: false,
        generationProgress: 0,
        generationTaskId: null,
        generatedVideo: null,
        errorMessage: '',
      };
    case 'SET_ERROR_MESSAGE':
      return { ...state, errorMessage: action.payload };
    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case 'RESET_SETTINGS':
      return { ...state, settings: { ...defaultSettings } };
    case 'DELETE_HISTORY_ITEM':
      return {
        ...state,
        generationHistory: state.generationHistory.filter((item) => item.id !== action.payload),
      };
    case 'LOAD_HISTORY_VIDEO':
      return { ...state, generatedVideo: action.payload.videoUrl };
    case 'SET_UPSCALE_VIDEO':
      return { ...state, upscaleVideo: action.payload.videoUrl };
    case 'SET_ADD_SOUND_VIDEO':
      return { ...state, addSoundVideo: action.payload.videoUrl };
    default:
      return state;
  }
}

const AppContext = createContext<{ state: AppState; actions: any } | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const actions = {
    setUploadedImage: (image: { file: File; preview: string }) =>
      dispatch({ type: 'SET_UPLOADED_IMAGE', payload: image }),
    removeUploadedImage: () => dispatch({ type: 'REMOVE_UPLOADED_IMAGE' }),
    setUploadedSecondImage: (image: { file: File; preview: string }) =>
      dispatch({ type: 'SET_UPLOADED_SECOND_IMAGE', payload: image }),
    removeUploadedSecondImage: () => dispatch({ type: 'REMOVE_UPLOADED_SECOND_IMAGE' }),
    setPrompt: (prompt: string) => dispatch({ type: 'SET_PROMPT', payload: prompt }),
    startGeneration: (taskId: string) =>
      dispatch({ type: 'START_GENERATION', payload: { taskId } }),
    updateGenerationProgress: (progress: number) =>
      dispatch({ type: 'UPDATE_GENERATION_PROGRESS', payload: progress }),
    completeGeneration: (videoUrl: string) =>
      dispatch({ type: 'COMPLETE_GENERATION', payload: { videoUrl } }),
    cancelGeneration: () => dispatch({ type: 'CANCEL_GENERATION' }),
    setError: (errorMessage: string) =>
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: errorMessage }),
    setActiveTab: (tabIndex: number) => dispatch({ type: 'SET_ACTIVE_TAB', payload: tabIndex }),
    resetForm: () => dispatch({ type: 'RESET_FORM' }),
    prepareNewGeneration: () => dispatch({ type: 'PREPARE_NEW_GENERATION' }),
    prepareNewGenerationNoHistory: () => dispatch({ type: 'PREPARE_NEW_GENERATION_NO_HISTORY' }),
    updateSettings: (settings: Partial<Settings>) =>
      dispatch({ type: 'UPDATE_SETTINGS', payload: settings }),
    resetSettings: () => dispatch({ type: 'RESET_SETTINGS' }),
    deleteHistoryItem: (id: string) => dispatch({ type: 'DELETE_HISTORY_ITEM', payload: id }),
    loadHistoryVideo: (videoUrl: string) =>
      dispatch({ type: 'LOAD_HISTORY_VIDEO', payload: { videoUrl } }),
    setUpscaleVideo: (videoUrl: string) =>
      dispatch({ type: 'SET_UPSCALE_VIDEO', payload: { videoUrl } }),
    setAddSoundVideo: (videoUrl: string) =>
      dispatch({ type: 'SET_ADD_SOUND_VIDEO', payload: { videoUrl } }),
  } as const;

  return <AppContext.Provider value={{ state, actions }}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) throw new Error('useAppContext must be used within an AppProvider');
  return context;
};

export default AppContext;
